import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { ApdmCampaignWizard } from '@euler/app/flows/apdm/campaigns/wizard/ApdmCampaignWizard';
import { WizardPreview } from '@euler/app/flows/apdm/campaigns/wizard/WizardPreview';
import { ReviewAction } from '@euler/app/flows/apdm/imports/ReviewControls';
import { useApdmCampaignWizard } from '@euler/functions/apdm/useApdmCampaignWizard';
import { canManageApdmImports } from '@euler/functions/apdmAccess';
import { useUserProfileInfo } from '@euler/functions/user/useUserProfileInfo';
import {
  ApdmCampaignPreviewResult,
  ApdmCampaignVersionRef,
} from '@euler/model/apdm';
import { parseApdmCampaignPreviewWidgetPayload } from '@euler/model/apdm/widgets';
import { useServiceFactory } from '@euler/services/factory';
import { useEffect, useState } from 'react';
import { Text, View } from 'react-native';
import { useTheme } from 'styled-components';

function InlineReview({
  refValue,
  storeId,
}: {
  refValue: ApdmCampaignVersionRef;
  storeId: number;
}) {
  const wizard = useApdmCampaignWizard(storeId, refValue.campaignId);
  const navigation = useAppNavigation();
  const theme = useTheme();
  const [admission, setAdmission] = useState<'loading' | 'allowed' | 'stale'>(
    'loading',
  );
  useEffect(() => {
    if (admission === 'loading' && wizard.state.campaign)
      setAdmission(
        wizard.state.campaign.version === refValue.expectedVersion
          ? 'allowed'
          : 'stale',
      );
  }, [admission, wizard.state.campaign, refValue.expectedVersion]);
  return (
    <View>
      <ReviewAction
        title="打开活动编辑"
        onPress={() =>
          navigation.navigate('ApdmCampaignWizard', {
            campaignId: refValue.campaignId,
          })
        }
      />
      {admission === 'allowed' ? (
        <ApdmCampaignWizard
          wizard={wizard}
          reviewOnly
          onImport={() => navigation.navigate('ApdmImport', {})}
        />
      ) : (
        <Text style={{ color: theme.label.color }}>
          {admission === 'stale'
            ? '活动已更新，请打开活动编辑核对最新版本。'
            : wizard.state.error ?? '正在读取草稿与权限…'}
        </Text>
      )}
    </View>
  );
}
function Preview({
  refValue,
  streaming,
  storeId,
}: {
  refValue: ApdmCampaignVersionRef;
  streaming: boolean;
  storeId: number;
}) {
  const { campaignId, expectedVersion } = refValue;
  const { apdmCampaignService } = useServiceFactory();
  const navigation = useAppNavigation();
  const theme = useTheme();
  const [preview, setPreview] = useState<ApdmCampaignPreviewResult | null>(
    null,
  );
  const [failed, setFailed] = useState(false);
  const [revision, setRevision] = useState(0);
  const [reviewOpen, setReviewOpen] = useState(false);
  useEffect(() => {
    let active = true;
    setPreview(null);
    setFailed(false);
    if (!streaming)
      void apdmCampaignService
        .preview({ campaignId, expectedVersion })
        .then(value => {
          if (!active) return;
          if (
            value.campaign.id !== campaignId ||
            value.campaign.version !== expectedVersion
          ) {
            setFailed(true);
            return;
          }
          setPreview(value);
        })
        .catch(() => {
          if (active) setFailed(true);
        });
    return () => {
      active = false;
    };
  }, [apdmCampaignService, campaignId, expectedVersion, revision, streaming]);
  if (reviewOpen) return <InlineReview refValue={refValue} storeId={storeId} />;
  return (
    <View style={{ paddingVertical: 12 }}>
      <Text
        style={{
          color: theme.label.color,
          fontWeight: '600',
          marginBottom: 12,
        }}
      >
        活动预览
      </Text>
      {preview ? (
        <WizardPreview preview={preview} />
      ) : (
        <Text style={{ color: theme.label.color }}>
          {streaming
            ? '草稿整理完成后显示预览。'
            : failed
            ? '活动已更新或预览暂不可用，请打开活动页面查看最新内容。'
            : '正在获取服务器预览…'}
        </Text>
      )}
      {failed && (
        <ReviewAction
          title="重新获取预览"
          onPress={() => setRevision(value => value + 1)}
        />
      )}
      <ReviewAction
        title="核对并启动活动"
        disabled={streaming || !preview || failed}
        onPress={() => setReviewOpen(true)}
      />
      <ReviewAction
        title="打开活动编辑"
        disabled={streaming}
        onPress={() =>
          navigation.navigate('ApdmCampaignWizard', {
            campaignId: refValue.campaignId,
          })
        }
      />
    </View>
  );
}
export function ApdmCampaignPreviewWidget({
  data,
  streaming = false,
}: {
  data: unknown;
  streaming?: boolean;
}) {
  const profile = useUserProfileInfo();
  const theme = useTheme();
  const refValue = parseApdmCampaignPreviewWidgetPayload(data);
  if (!refValue)
    return (
      <Text style={{ color: theme.label.color }}>
        活动预览引用无效，请重新获取活动。
      </Text>
    );
  if (!canManageApdmImports(profile ?? null))
    return (
      <Text style={{ color: theme.label.color }}>
        当前账号无法查看活动预览。
      </Text>
    );
  return (
    <Preview
      key={`${profile!.user.userId}:${profile!.store.id}:${
        refValue.campaignId
      }:${refValue.expectedVersion}`}
      refValue={refValue}
      storeId={profile!.store.id}
      streaming={streaming}
    />
  );
}
